import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import { Box, Stack } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import { chatApi } from '../../api/chat';
import NotificationMessage from '../../containers/notification/notification-message';
import {
    baseAtom
} from "../../stores/jotai/base";
import { discoverySessionAtom } from '../../stores/jotai/discovery-session';
import { hypothesisToolbarAtom } from '../../stores/jotai/hypothesisToolbar';
import { interviewsAtom } from '../../stores/jotai/interviews/interviews';
import { notificationPopoverAtom } from '../../stores/jotai/notification/notificationPopover';
import { markNotificationAsReadByIDs, markNotificationAsUnreadByIDs } from '../../util/notificationAPIHelpers';
import useSwitchTeam from '../app/hooks/useSwitchTeam';
import { teamsAtom } from "../../stores/jotai/teams/teams";

const ROOT_PANEL_STYLE = {
    width: "100%",
};

export const NOTIFICATION_LIST_STYLE = {
    height: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    padding: "6px 2px 6px 6px",
    ".MuiDivider-root:last-child": {
        display: "none",
    }
};

function NotificationListener(props) {
    const { closeNotificationPopover, notifications, isNotificationPopoverOpen } = props;
    const discoverySession = useAtomValue(discoverySessionAtom);
    const [base, setBase] = useAtom(baseAtom);
    const setHypothesisToolbarAtomState = useSetAtom(hypothesisToolbarAtom);
    const [sid, setSid] = useState(undefined);
    const [sessionChannels, setSessionChannels] = useState([]);
    const notificaitonPopoverAtomState = useAtomValue(notificationPopoverAtom);
    const [sessionCache, setSessionCache] = useState(undefined);
    const [teams,setTeams]=useAtom(teamsAtom);

    const {team, cohorts, user} = base;
    const { showOnlyUnreadNotifications } = notificaitonPopoverAtomState;
    const { switchTeam } = useSwitchTeam();
    const interviews = useAtomValue(interviewsAtom);
    const client=useQueryClient();
    const theme = useTheme();
    const initalizeChannels = () => {
        const legacyChannels = chatApi.getSubscribableChannels(
            undefined,
            // @ts-ignore
            base.user,
            base.team,
            base.cohort,
            teams,
            base.roleSnapshot
        );

        let newChannels = [
            `${team.groupID}-linx`, `${user.userID}-linx`,
            ...legacyChannels
        ]

        setSessionChannels(newChannels)
    }

    const handleMarkNotificationAsRead = (notificaiton, onDone: Function = noop) => {
        const notificationIDs: string[] = [notificaiton.notificationID];

        markNotificationAsReadByIDs(notificationIDs, discoverySession, () => {
            onDone();
            client.invalidateQueries({ queryKey: ["notifications"] });
        });
    }

    const handleMarkNotificationAsUnread = (
        notificaiton,
        onDone: Function = noop
    ) => {
        const notificationIDs: string[] = [notificaiton.notificationID];

        markNotificationAsUnreadByIDs(notificationIDs, discoverySession, () => {
            client.invalidateQueries({ queryKey: ["notifications"] });
            onDone();
        });
    };

    const getNotificationVisibilityState = (notificaiton) => {
        const isShowableByUnreadFilter = showOnlyUnreadNotifications
            ? notificaiton.seen !== 1
            : true;

        return isShowableByUnreadFilter;
    }

    const renderEmptyNotificationScreen = () => {
        return (
            <Stack
                sx={{
                    padding: "40px",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <AnnouncementOutlinedIcon
                    sx={{
                        fontSize: "150px",
                        color: theme?.custom?.disabledColor,
                    }}
                />
                <Box
                    sx={{
                        marginTop: "10px",
                    }}
                >
                    You have no notifications here.
                </Box>
            </Stack>
        );
    }

    const renderNotificationList = () => {
        const filteredNotifications = notifications.filter((notificaiton) =>
            getNotificationVisibilityState(notificaiton)
        );

        return (
            <Box
                className="NotificationListScroller"
                sx={NOTIFICATION_LIST_STYLE}
            >
                {filteredNotifications?.length > 0
                    ? filteredNotifications?.map((notificaiton) => (
                            <NotificationMessage
                                key={notificaiton.notificationID}
                                interviews={interviews}
                                isNotificationPopoverOpen={isNotificationPopoverOpen}
                                teams={teams}
                                findings={props.findings}
                                cohorts={cohorts}
                                messageData={notificaiton}
                                user={user}
                                base={base}
                                setBase={setBase}
                                setHypothesisToolbarAtomState={
                                    setHypothesisToolbarAtomState
                                }
                                closeNotificationPopover={closeNotificationPopover}
                                switchTeam={switchTeam}
                                handleMarkNotificationAsRead={(onDone: Function) => {
                                    handleMarkNotificationAsRead(notificaiton, onDone);
                                }}
                                handleMarkNotificationAsUnread={(onDone: Function) => {
                                    handleMarkNotificationAsUnread(notificaiton, onDone);
                                }}
                                theme={theme}
                            />
                        ))
                    : renderEmptyNotificationScreen()}
            </Box>
        );
    };

    useEffect(() => {
        if (team) {
            // check for first time setup
            if (sessionChannels?.length === 0) {
                setSessionCache(team?.groupID);
                initalizeChannels();
            }
            else {
                // check for team change
                if (sessionCache !== team?.groupID) {
                    setSessionCache(team?.groupID);
                    initalizeChannels();
                }
            }
        }
    }, [sid, team, sessionCache]);

    // handle session setup
    useEffect(() => {
        // console.log('setting up session');
        const newSID = discoverySession?.sessionID;
        setSid(newSID);
        try {
            //@ts-ignore
            window.__sid = newSID;
        }
        catch (e) { }
        // console.log(`session id ( ${newSID} )`)
    }, [discoverySession]);

    return (
        <Box
            className="NotificationList"
            sx={ROOT_PANEL_STYLE}
        >
            {renderNotificationList()}
        </Box>
    );
}

NotificationListener.defaultProps = {
	closeNotificationPopover: noop,
	notifications: [],
    isNotificationPopoverOpen: false,
};

export default NotificationListener;
